/**
 * The drop overlay — the half of adaptive layout you can see.
 *
 * `drop.ts` answers "where may this go?" and holds no DOM type. This file is
 * the other side of that split: it turns the answer into rectangles on a
 * screen, handles the pointer, and hands the committed spec back. Same shape as
 * `edit.ts` / `edit-host.ts`, for the same reason — there is more than one
 * conformant renderer, and the engine must not know which one it is talking to.
 *
 * What the user asked for, and the sentence the design is measured against:
 *
 * > when you grab a block in notion and all different sections get highlighted
 * > where you can put them and how
 *
 * **All of them, at drag start.** Not the nearest one as the pointer arrives —
 * every legal position lit at once, so the answer to "where can this go" is
 * available before the user has committed to a direction. That is the whole
 * difference between a drop target and adaptive layout, and it is affordable
 * only because `enumerateDrops` is arithmetic rather than a speculative compile.
 *
 * Refusals are drawn too, in a different key. A place that stays dark is
 * indistinguishable from a place nobody thought about; a place drawn as refused,
 * with a reason on hover, is the system telling the truth about its own rules.
 */

import {
  enumerateDrops, applyDrop, specNodeAt,
  type DropTarget, type Payload, type StructuralAuthority,
} from './drop.js';
import { ANCHOR_ATTR } from './edit.js';
import type { IR } from './ir.js';
import type { TLayoutSpec } from '@morph/spec';

export interface DropHostOptions {
  /** The element the renderer painted into. */
  readonly root: HTMLElement;
  /** Current spec and IR. Read at drag start, never cached across a repaint. */
  readonly current: () => { readonly spec: TLayoutSpec; readonly ir: IR } | undefined;
  readonly authority: StructuralAuthority;
  /** Called with the committed spec. The host decides what to do with it —
   *  this file does not re-render, because deciding when to repaint is the
   *  application's business and not the overlay's. */
  readonly onCommit: (spec: TLayoutSpec, target: DropTarget) => void;
  readonly onRefusal?: (message: string) => void;
  /** Announced to a live region. Drag and drop is invisible to a screen reader
   *  otherwise, and "unavailable" is not the same as "absent". */
  readonly onStatus?: (message: string) => void;
}

export interface DropHost {
  detach(): void;
  /** For tests and for a keyboard path: run a drag without a pointer. */
  targetsFor(nodeIndex: number): DropTarget[];
}

/** Movement before a press becomes a drag. Below this, a press is a click, and
 *  a card that starts dragging on a 2px twitch cannot be tapped. */
const DRAG_THRESHOLD_PX = 6;

const OVERLAY_CLASS = 'm-drop-layer';

export function attachDropHost(opts: DropHostOptions): DropHost {
  const { root } = opts;

  let layer: HTMLElement | undefined;
  let dragging: {
    payload: Payload; targets: DropTarget[]; painted: DropTarget[]; from: number;
  } | undefined;
  let pressed: { x: number; y: number; node: number } | undefined;
  let active: { target: DropTarget; el: HTMLElement } | undefined;

  /** Node index → the element the renderer emitted for it. Built per drag,
   *  because a repaint replaces every element and a cached map would point at
   *  detached nodes — the same staleness class the edit path already carries a
   *  comment about. */
  function elementIndex(): Map<number, HTMLElement> {
    const map = new Map<number, HTMLElement>();
    for (const el of root.querySelectorAll<HTMLElement>(`[${ANCHOR_ATTR.node}]`)) {
      const i = Number(el.getAttribute(ANCHOR_ATTR.node));
      if (Number.isInteger(i) && !map.has(i)) map.set(i, el);
    }
    return map;
  }

  function ensureLayer(): HTMLElement {
    if (layer?.isConnected) return layer;
    layer = document.createElement('div');
    layer.className = OVERLAY_CLASS;
    layer.setAttribute('aria-hidden', 'true');   // the live region carries the meaning
    root.appendChild(layer);
    return layer;
  }

  function clear(): void {
    layer?.remove();
    layer = undefined;
    active = undefined;
    root.removeAttribute('data-morph-dragging');
  }

  /**
   * Draw every candidate at once.
   *
   * A `before`/`after` candidate is a LINE at the edge of the sibling it names;
   * an `into` is the host's own box; a `place` is a strip along the surface
   * edge. Each is positioned relative to the render root, so the overlay does
   * not care where on the page the root sits.
   */
  function paint(
    targets: DropTarget[],
    els: Map<number, HTMLElement>,
  ): DropTarget[] {
    const el = ensureLayer();
    el.textContent = '';
    const base = root.getBoundingClientRect();
    // Container identity and insertion position are different questions, so
    // they are different marks. One quiet outline names each candidate host;
    // the line/box above it says where inside that host the move would land.
    const markedContainers = new Set<number>();
    // The targets that actually got a rectangle, in the order they were drawn.
    // `pick` used to index the full target list by zone position, which silently
    // desynchronised the moment one target's host had no element — and then the
    // zone the user saw highlighted was not the drop that would commit.
    const painted: DropTarget[] = [];

    for (const t of targets) {
      const host = els.get(t.host);
      if (!host) continue;
      const box = host.getBoundingClientRect();
      const top = box.top - base.top;
      const left = box.left - base.left;

      if (!markedContainers.has(t.host)) {
        const container = document.createElement('div');
        container.className = 'm-drop-container';
        container.dataset['host'] = String(t.host);
        Object.assign(container.style, {
          top: `${top}px`, left: `${left}px`,
          width: `${box.width}px`, height: `${box.height}px`,
        });
        el.appendChild(container);
        markedContainers.add(t.host);
      }

      const zone = document.createElement('div');
      zone.className = 'm-drop-zone m-drop-position';
      zone.dataset['relation'] = t.relation;
      zone.dataset['ok'] = String(t.ok);
      if (t.why) {
        zone.title = t.why;
        // The layer deliberately ignores pointer events, so a native title can
        // never be hovered. Keep the reason as data for the visible active label.
        zone.dataset['reason'] = t.why;
      }
      if (t.destroys?.length) zone.dataset['destroys'] = String(t.destroys.length);

      if (t.relation === 'place' && t.placement) {
        const side = t.placement.mode === 'docked' ? t.placement.edge : t.placement.rest;
        zone.dataset['side'] = side;
        zone.classList.add('m-drop-place');
      } else if (t.relation === 'into') {
        Object.assign(zone.style, {
          top: `${top}px`, left: `${left}px`,
          width: `${box.width}px`, height: `${box.height}px`,
        });
      } else if (t.relation === 'beside') {
        /**
         * A BESIDE HUGS THE BLOCK IT PAIRS WITH, down its inline-end edge.
         *
         * It used to fall through to the line below, which draws `before` and
         * `after` — and `beside` shares its host AND its index with the `after`
         * it sits next to, so the two got the SAME RECTANGLE. `pick` takes the
         * nearest zone centre with a strict `<`, so a tie goes to whichever was
         * painted first, and `after` is always enumerated first.
         *
         * Measured on the deployed build once regions entered the element index:
         * six `beside` zones were drawn and **not one had a centre of its own** —
         * every one was exactly coincident with an `after`. A person could not
         * have selected a column with a pointer at any point, however carefully
         * they aimed.
         *
         * So it is drawn where it MEANS: down the trailing edge of the block it
         * would pair with, rather than in the gap after it. `pairWith` is the
         * node, which is what the applier needs too — after the wrap there is
         * one child where there were two, so the gap index cannot say which.
         *
         * The stylesheet already drew this differently — `border-radius` and an
         * inline-start border, with a comment that *"a beside zone that looked
         * the same would be two identical marks"*. The paint agreed with that
         * intent and the GEOMETRY never did.
         */
        /**
         * The paired block, by ELEMENT INDEX where it is there and by the
         * host's own DOM children where it is not.
         *
         * The first version required `els.get(t.pairWith)` and fell through to
         * the line when it was missing — which left two of six zones coincident
         * again, because a collection carries no node attribute either and so is
         * absent from the index for exactly the same reason a region was. Rather
         * than chase which kinds are indexed, the fallback asks the host for the
         * child at the gap's index minus one, which is what `beside` pairs with
         * by construction and is always in the DOM.
         */
        const paired = (t.pairWith !== undefined ? els.get(t.pairWith) : undefined)
          ?? host.children[Math.max(0, (t.index ?? 1) - 1)];
        const sib = (paired ?? host).getBoundingClientRect();
        // A strip, inset so it cannot swallow the whole block: what is being
        // offered is the edge, and an edge that covers its own block reads as
        // `into`.
        const w = Math.max(10, Math.min(28, sib.width * 0.18));
        Object.assign(zone.style, {
          top: `${sib.top - base.top}px`,
          left: `${sib.right - w - base.left}px`,
          width: `${w}px`, height: `${sib.height}px`,
        });
      } else {
        // A line. Which edge depends on how the host lays its children out:
        // a row puts siblings side by side, so the insertion point is vertical.
        const horizontal = getComputedStyle(host).flexDirection !== 'row';
        const child = host.children[Math.min(t.index ?? 0, host.children.length - 1)];
        const cb = (child ?? host).getBoundingClientRect();
        const after = (t.index ?? 0) > 0;
        Object.assign(zone.style, horizontal
          ? { top: `${(after ? cb.bottom : cb.top) - base.top}px`,
              left: `${cb.left - base.left}px`, width: `${cb.width}px`, height: '3px' }
          : { top: `${cb.top - base.top}px`,
              left: `${(after ? cb.right : cb.left) - base.left}px`,
              width: '3px', height: `${cb.height}px` });
      }
      el.appendChild(zone);
      painted.push(t);
    }
    return painted;
  }

  /** The candidate under the pointer. Nearest by centre distance among the ones
   *  actually drawn, so what lights up is what commits. */
  function pick(x: number, y: number): { target: DropTarget; el: HTMLElement } | undefined {
    if (!layer || !dragging) return undefined;
    let best: { target: DropTarget; el: HTMLElement; d: number } | undefined;
    const zones = layer.querySelectorAll<HTMLElement>('.m-drop-zone');
    zones.forEach((zone, i) => {
      const t = dragging!.painted[i];
      if (!t) return;
      const b = zone.getBoundingClientRect();
      const dx = x - (b.left + b.width / 2);
      const dy = y - (b.top + b.height / 2);
      const d = dx * dx + dy * dy;
      if (!best || d < best.d) best = { target: t, el: zone, d };
    });
    return best ? { target: best.target, el: best.el } : undefined;
  }

  const SIDE_PHRASE: Record<string, string> = {
    'block-start': 'along the top', 'block-end': 'along the bottom',
    'inline-start': 'down the start edge', 'inline-end': 'down the end edge',
    center: 'floating in the middle',
  };

  /** What this drop would do, as a sentence. It goes to a live region, so it
   *  has to read as English rather than as a node index — the first version
   *  announced "into 1", which is true and useless. */
  function describe(t: DropTarget): string {
    if (!t.ok) return `Can't move it here. ${t.why ?? 'This position is not allowed.'}`;
    const lost = t.destroys?.length
      ? ` It would stop showing ${t.destroys.length} thing${t.destroys.length > 1 ? 's' : ''}.`
      : '';
    const becomes = t.consequence ? ` The collection becomes a ${t.consequence}.` : '';
    if (t.relation === 'place' && t.placement) {
      const side = t.placement.mode === 'docked' ? t.placement.edge : t.placement.rest;
      return `Dock it ${SIDE_PHRASE[side] ?? side}.${lost}`;
    }
    const where = t.relation === 'into' ? 'Put it inside this block'
      : t.relation === 'beside' ? 'Put it beside this block, as a column'
      : t.relation === 'before' ? 'Put it above this block'
        : 'Put it below this block';
    return `${where}.${becomes}${lost}`;
  }

  function onPointerDown(e: PointerEvent): void {
    const el = (e.target as HTMLElement | null)?.closest?.<HTMLElement>(`[${ANCHOR_ATTR.node}]`);
    if (!el || e.button !== 0) return;
    // Never start a drag from inside a caret. Editing is the other interaction
    // that owns a press, and it was here first.
    if (el.closest('[contenteditable="true"]')) return;
    const node = Number(el.getAttribute(ANCHOR_ATTR.node));
    if (!Number.isInteger(node)) return;
    pressed = { x: e.clientX, y: e.clientY, node };
  }

  function begin(node: number): boolean {
    const cur = opts.current();
    if (!cur) return false;
    const n = cur.ir.nodes[node];
    if (!n) return false;
    // A leaf carries a value and is not a block a user arranges. Dragging a
    // single field out of a card is a different feature with a different
    // meaning, and offering it here would promise something this does not do.
    if (n.kind === 'field' || n.kind === 'media' || n.kind === 'node-slot') return false;

    // The REAL subtree, not a stub. A stub `{ kind, id }` is not a spec — a
    // card with no bindings and a region with no children — and every drop was
    // refused by the validator with a wall of `shape` problems until a real
    // drag in a real browser showed it.
    const moving = specNodeAt(cur.spec, cur.ir, node);
    if (!moving) return false;
    const payload: Payload = { kind: n.kind, node: moving, from: node };
    const targets = enumerateDrops(cur.ir, payload);
    root.setAttribute('data-morph-dragging', 'true');
    dragging = { payload, targets, painted: [], from: node };
    dragging.painted = paint(targets, elementIndex());
    const legal = targets.filter((t) => t.ok).length;
    opts.onStatus?.(`Moving a ${n.kind}. ${legal} place${legal === 1 ? '' : 's'} it can go.`);
    return true;
  }

  function onPointerMove(e: PointerEvent): void {
    if (!dragging) {
      if (!pressed) return;
      if (Math.hypot(e.clientX - pressed.x, e.clientY - pressed.y) < DRAG_THRESHOLD_PX) return;
      if (!begin(pressed.node)) { pressed = undefined; return; }
    }
    const next = pick(e.clientX, e.clientY);
    if (next?.el === active?.el) return;
    active?.el.removeAttribute('data-morph-active');
    active = next;
    if (active) {
      active.el.setAttribute('data-morph-active', 'true');
      opts.onStatus?.(describe(active.target));
    }
  }

  function onPointerUp(): void {
    const drag = dragging;
    const chosen = active?.target;
    pressed = undefined;
    dragging = undefined;
    clear();
    if (!drag || !chosen) return;

    if (!chosen.ok) {
      const message = chosen.why ?? 'this position is not allowed';
      opts.onRefusal?.(message);
      opts.onStatus?.(`Move refused. ${message}`);
      return;
    }

    const cur = opts.current();
    if (!cur) return;
    const out = applyDrop(cur.spec, cur.ir, chosen, drag.payload, opts.authority);
    if (!out.ok) {
      opts.onRefusal?.(`${out.code}: ${out.message}`);
      return;
    }
    opts.onCommit(out.value, chosen);
  }

  function onKeyDown(e: KeyboardEvent): void {
    // Escape abandons a drag. Without it the only way out of one is to complete
    // it somewhere, which makes an accidental drag unrecoverable.
    if (e.key !== 'Escape' || !dragging) return;
    pressed = undefined;
    dragging = undefined;
    clear();
    opts.onStatus?.('Move cancelled.');
  }

  root.addEventListener('pointerdown', onPointerDown);
  window.addEventListener('pointermove', onPointerMove);
  window.addEventListener('pointerup', onPointerUp);
  window.addEventListener('keydown', onKeyDown);

  return {
    detach() {
      clear();
      root.removeEventListener('pointerdown', onPointerDown);
      window.removeEventListener('pointermove', onPointerMove);
      window.removeEventListener('pointerup', onPointerUp);
      window.removeEventListener('keydown', onKeyDown);
    },
    targetsFor(nodeIndex: number) {
      const cur = opts.current();
      if (!cur) return [];
      const n = cur.ir.nodes[nodeIndex];
      if (!n) return [];
      const moving = specNodeAt(cur.spec, cur.ir, nodeIndex);
      if (!moving) return [];
      return enumerateDrops(cur.ir, { kind: n.kind, node: moving, from: nodeIndex });
    },
  };
}

/**
 * The overlay's stylesheet.
 *
 * Kept beside the host rather than in `BASE_CSS`, because a renderer that does
 * not offer dragging should not carry the cost of styling it. Colours come from
 * the same custom properties the renderer resolves — the overlay invents no
 * palette, which is the rule the whole runtime works under.
 */
export const DROP_CSS = `
.m-drop-layer{position:absolute;inset:0;pointer-events:none;z-index:60}
.m-root{position:relative}
[data-morph-dragging="true"]{cursor:grabbing;user-select:none}

/* The container is the quiet outer channel; the drop position is the stronger
   inner channel. Conflating them makes an insertion line look like a selected
   block and leaves the parent relationship invisible. */
.m-drop-container{position:absolute;z-index:0;border-radius:10px;
  outline:1px solid color-mix(in oklab,var(--solid) 42%,transparent);
  box-shadow:inset 0 0 0 2px color-mix(in oklab,var(--page) 70%,transparent);
  background:transparent}
.m-drop-zone{position:absolute;z-index:1;border-radius:6px;transition:opacity .1s ease}
.m-drop-zone[data-ok="true"]{background:color-mix(in oklab,var(--solid) 30%,transparent);
  outline:2px dashed color-mix(in oklab,var(--solid) 75%,transparent);opacity:.7}
/* Refused is drawn, not hidden. A place that stays dark is indistinguishable
   from a place nobody thought about; hatched, with the reason on hover, is the
   system telling the truth about its own rules. */
.m-drop-zone[data-ok="false"]{background:repeating-linear-gradient(45deg,
  color-mix(in oklab,var(--border-strong) 34%,transparent) 0 5px,transparent 5px 10px);
  outline:1px solid color-mix(in oklab,var(--border-strong) 45%,transparent);opacity:.5}
.m-drop-zone[data-morph-active="true"]{opacity:1;
  background:color-mix(in oklab,var(--text) 20%,transparent);
  outline:3px solid var(--text);outline-offset:1px;
  box-shadow:0 0 0 1px var(--page)}
.m-drop-zone[data-ok="false"][data-morph-active="true"]{
  background:repeating-linear-gradient(45deg,
    color-mix(in oklab,var(--text) 20%,transparent) 0 5px,transparent 5px 10px)}
/* The layer cannot receive hover events: it must not steal the drag. Reasons
   and blast radius therefore appear on the nearest active zone, while the same
   sentence is announced through the host's live status callback. */
.m-drop-zone[data-morph-active="true"][data-reason]::after{
  content:attr(data-reason);position:absolute;inset-inline-start:0;
  inset-block-start:calc(100% + 7px);z-index:2;padding:4px 7px;border-radius:5px;
  background:var(--text);color:var(--page);font:600 11px/1.25 system-ui,sans-serif;
  white-space:nowrap;box-shadow:0 2px 8px color-mix(in oklab,var(--text) 18%,transparent)}
/* A drop that would stop rendering something is drawn differently BEFORE it
   happens, not explained afterwards. */
.m-drop-zone[data-destroys]{outline-style:dashed}
.m-drop-zone[data-relation="into"]{border-radius:10px}
/* A COLUMN reads vertically, because that is the edge the block will sit
   against. The above and below zones are horizontal rules between stacked
   blocks; a beside zone that looked the same would be two identical marks
   meaning "above" and "next to".
   No backticks in this block: it is inside a template literal, and one of them
   ends it. Third time in this session. */
.m-drop-zone[data-relation="beside"]{border-radius:10px;
  border-inline-start-width:3px}

.m-drop-place{inset:auto}
.m-drop-place[data-side="block-start"]{top:0;left:0;right:0;height:12%}
.m-drop-place[data-side="block-end"]{bottom:0;left:0;right:0;height:12%}
.m-drop-place[data-side="inline-start"]{top:0;bottom:0;inset-inline-start:0;width:12%}
.m-drop-place[data-side="inline-end"]{top:0;bottom:0;inset-inline-end:0;width:12%}
.m-drop-place[data-side="center"]{top:35%;left:35%;width:30%;height:30%}

@media (prefers-reduced-motion: reduce){.m-drop-zone{transition:none}}
`;
